import React from 'react';
import { Button, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faSave,
    faFileInvoiceDollar,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormGroupColumn,
    GroupedRow,
    Input,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { QuickBooksInventoryItemDTO } from './QuickBooksInventoryItem';
import { QBIICompanyChargeTypeGrid } from './QBIICompanyChargeTypeGrid';
import { QBIIContractChargeTypeGrid } from './QBIIContractChargeTypeGrid';
import { QBIIChargeTypeGrid } from './QBIIChargeTypeGrid';

class QuickBooksInventoryItemForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            originalData: new QuickBooksInventoryItemDTO(),
            inventoryItem: new QuickBooksInventoryItemDTO(),
            salesAccounts: [],
            loading: true,
            saving: false,
        };
        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(this.state.inventoryItem);

        if (this.props.location.pathname == AppNavPaths.QuickBooksInventoryItemNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (inventoryItem) => {
        inventoryItem.id = 0;

        const response = await httpClient
            .post(ApiRoutes.quickbooks.create(), inventoryItem)
            .catch(this.handleSaveError);

        if (response && !isNaN(response.id)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );

            this.context.history.push(
                `${AppNavPaths.QuickBooksInventoryItem}/${response.id}`,
            );
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;

        const [salesAccounts, inventoryItem] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.quickBooksSalesAccounts()),
            id
                ? httpClient.js(ApiRoutes.quickbooks.edit(id))
                : new QuickBooksInventoryItemDTO(),
        ]);

        const originalData = clone(inventoryItem);

        this.setState({
            originalData,
            inventoryItem: originalData,
            salesAccounts,
            loading: false,
            saving: false,
        });
    }

    update = async (inventoryItem) => {
        const response = await httpClient
            .put(ApiRoutes.quickbooks.update(inventoryItem.id), inventoryItem)
            .catch(this.handleSaveError);

        if (response && parseInt(response.id ?? 0) >= 0) {
            this.setState({
                inventoryItem: response,
                originalData: response /* also restore this to reset Prompt */,
                saving: false,
                formValidated: false,
            });

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Inventory Item saved."
                />,
            );
        }

        this.setSaving(false);
    };

    renderForm() {
        const {
            salesAccounts,
            inventoryItem,
            originalData,
            saving,
            errors,
            loading,
            formValidated,
        } = this.state;

        const newInventoryItem = parseInt(inventoryItem.id ?? 0) <= 0;

        if (!inventoryItem) {
            return '';
        }

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, inventoryItem)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="quickBooksInventoryItemForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={
                        newInventoryItem
                            ? 'New Inventory Item'
                            : 'Edit Inventory Item'
                    }
                    formName="quickBooksInventoryItemForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.QuickBooksInventoryItems}
                    backLabel="Back to Inventory Items"
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Inventory Item Name"
                                id="name"
                                name="inventoryItem.name"
                                value={inventoryItem.name ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Name"
                                error="Name is required."
                                required
                            />

                            <ValidatedSelect
                                id="salesAccountId"
                                name="inventoryItem.salesAccountId"
                                label="Sales Account"
                                required
                                options={salesAccounts}
                                value={
                                    (salesAccounts ?? []).find(
                                        (s) => s.value
                                            === inventoryItem.salesAccountId,
                                    ) ?? ''
                                }
                                onChange={this.handlers.select.bind(this)}
                                error="A Sales Account selection is required."
                            />

                            <Input
                                label="NetSuite ID"
                                id="externalId"
                                name="inventoryItem.externalId"
                                value={inventoryItem.externalId ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter NetSuite ID"
                            />
                            <Input
                                label="Intercompany NetSuite ID"
                                id="intercompanyExternalId "
                                name="inventoryItem.intercompanyExternalId"
                                value={inventoryItem.intercompanyExternalId ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Intercompany NetSuite ID"
                            />
                        </FormGroupColumn>
                    </GroupedRow>
                    {!!inventoryItem.id && (
                        <>
                            <SubHeading>Charge Types</SubHeading>
                            <GroupedRow>
                                <QBIIChargeTypeGrid
                                    chargeTypes={
                                        inventoryItem.chargeTypeBillingRates
                                    }
                                />
                            </GroupedRow>
                            <SubHeading>Contract Charge Types</SubHeading>
                            <GroupedRow>
                                <QBIIContractChargeTypeGrid
                                    contractChargeTypes={
                                        inventoryItem.contractChargeTypeBillingRates
                                    }
                                />
                            </GroupedRow>
                            <SubHeading>Company Charge Types</SubHeading>
                            <GroupedRow>
                                <QBIICompanyChargeTypeGrid
                                    companyChargeTypes={
                                        inventoryItem.companyChargeTypeBillingRates
                                    }
                                />
                            </GroupedRow>
                        </>
                    )}
                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="quickBooksInventoryItemForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {newInventoryItem
                                ? 'Save New Inventory Item'
                                : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }

    render() {
        const { loading } = this.state;
        if (loading) {
            return <Progress />;
        }
        return this.renderForm();
    }
}
export default QuickBooksInventoryItemForm;
